/**
 * 全站共享的登录态。
 *
 * 每个页面组件里都放了一份 <Navbar/>，而 Navbar 在 mounted() 里调 user-info。
 * SPA 里每次翻页都会重新挂载 Navbar，于是每翻一页就重发一次同样的请求。
 * 服务端有重复请求保护（3 秒内的相同请求直接 429 DUPLICATE_REQUEST），
 * 稍微点快一点就会被挡下来，导航栏因此时不时显示成未登录。
 *
 * 这里把登录态收成一份：
 *   - 并发调用共用同一个在途请求，不会同时发两次
 *   - 成功结果缓存 TTL 秒，期间翻页直接复用，不再打接口
 *   - 需要最新结果时用 force（例如刚登录完）
 *
 * 注意这只是「谁登录了」的缓存。真正的授权判断始终在服务端：下单、进个人中心
 * 这些接口自己会鉴权，缓存过期与否都不影响安全性。
 */
import config from '@/config.json'
import { post } from './utils'

const TTL = 60 * 1000

let cached = null      // 最近一次「有结论」的结果：登录成功，或服务端明确说未登录
let cachedAt = 0
let inflight = null    // 在途请求，用于合并并发调用

/** 服务端是否明确表示「未登录」（区别于 429 / 5xx / 断网这类没结论的失败） */
export function isUnauthenticated(data) {
  return !!data && (data.http_status === 401 || data.errno === 12)
}

export function fetchUser({ force = false } = {}) {
  if (!force && cached && Date.now() - cachedAt < TTL) {
    return Promise.resolve(cached)
  }
  if (inflight) return inflight

  inflight = post(`${config.api.userinfo}`, {}).then(data => {
    inflight = null
    // 登录成功和「明确未登录」都是结论，都可以缓存，翻页时不必反复问。
    // 429 / 5xx / 断网没有结论：保留上一次的结果，别把用户当成登出。
    if ((data && data.errno === 0) || isUnauthenticated(data)) {
      cached = data
      cachedAt = Date.now()
      return data
    }
    return cached || data
  }).catch(e => {
    inflight = null
    return cached || { errno: -1, http_status: 0, info: String(e) }
  })

  return inflight
}

/** 退出登录后调用，清掉内存里的登录态 */
export function resetUser() {
  cached = null
  cachedAt = 0
  inflight = null
}
